import { Bound, BoundSet } from "./boundSet";
import { zip } from "./itertools";

var pipeline = function (opts) {
    var conf = {
        chunkSize: 500, // number of records to cache
        url: "", // ajax script url
        data: null, // function or object with params to send to server (see ajax.data option in docs)
        dataSrc: null, // string or function for altering data recieved (see ajax.dataSrc option in docs)
        type: "GET", // http method to use
        ...opts,
    };

    const { data, dataSrc, chunkSize, ...ajaxOpts } = conf;

    // Private variables for storing the cache
    var cache;

    function emptyCache() {
        cache = {
            draw: null,
            recordsFiltered: null,
            recordsTotal: null,
            data: [],
            chunkSize: chunkSize,
            bounds: null,
            lastRequest: null,
        };
    }

    emptyCache();

    async function fetchChunks(request) {
        // apply data option to the request
        let reqBody;
        if (typeof data === "function") {
            let result = data(request);

            if (result === undefined) {
                reqBody = JSON.stringify(request);
            } else if (typeof result === "string" || result instanceof String) {
                reqBody = result;
            } else {
                reqBody = JSON.stringify(result);
            }
        } else if (data) {
            reqBody = JSON.stringify({ ...request, ...data });
        }

        // use jquery's $.ajax to make the request
        let response = await $.ajax({ ...ajaxOpts, data: reqBody });

        // apply dataSrc option to the response and save data
        if (typeof dataSrc === "function") {
            response.data = dataSrc(response);
        } else if (dataSrc) {
            response.data = response[dataSrc];
        }

        return response;
    }

    /**
     * Update/return the cache to be used for this request. If no updates needed then just return current.
     *
     * @param {Object} request The original request sent by DataTables
     * @returns
     */
    async function getUpdateCache(request) {
        // create bound object from request
        let drawBound = new Bound(request.start, request.start + request.length);

        // initial value of ranges is the ideal range of the full cache including buffers around the draw area
        let fullCacheBounds = new Bound(
            Math.max(Math.floor(drawBound.start / chunkSize) * chunkSize - chunkSize, 0),
            Math.ceil(drawBound.end / chunkSize) * chunkSize + chunkSize
        );

        let ranges = new BoundSet(fullCacheBounds.copy());
        if (
            cache.draw !== null &&
            JSON.stringify(request.order) === JSON.stringify(cache.lastRequest.order) &&
            JSON.stringify(request.columns) === JSON.stringify(cache.lastRequest.columns) &&
            JSON.stringify(request.search) === JSON.stringify(cache.lastRequest.search)
        ) {
            // if cache already exists, and order/columns/search are unchanged then subtract out existing cache range
            // this is the area of the cache that we actually need to fetch
            ranges = ranges.difference(cache.bounds);
        } else {
            emptyCache();
        }

        // if no changes needed then return current cache
        // TODO: next iteration should keep track of outgoing requests. If an pending request would cover this one then just return cache
        // could organize this by keying the Promise of the pending request to fullCacheBounds
        if (ranges.isNullSet()) return cache;

        // make temp cache in case current cache is overwritten by another ajax call
        // only if cache exists
        let newCache;
        if (cache.draw !== null) {
            let intersection = drawBound.intersection(cache.bounds)[0];
            if (intersection) {
                newCache = {
                    ...cache,
                    // force deep copy of only the required data
                    bounds: intersection,
                    data: JSON.parse(
                        JSON.stringify(
                            cache.data.slice(
                                intersection.start - cache.bounds.start,
                                intersection.end - cache.bounds.start
                            )
                        )
                    ),
                };
            }
        }

        // make ajax calls concurrently
        let responses = await Promise.all(
            ranges.bounds.map((bound) =>
                fetchChunks({ ...request, start: bound.start, length: bound.size })
            )
        );

        // if another ajax call beat this one to it then just combine the gathered data and return
        if (cache.draw === null || request.draw > cache.draw) {
            newCache = cache;
        }

        // otherwise update the cache and return it
        for (const [range, response] of zip(ranges, responses)) {
            const { data, ...responseWithoutData } = response;

            newCache = Object.assign(newCache, responseWithoutData);

            if (newCache.bounds === null) newCache.bounds = range;

            if (range.start < newCache.bounds.start) {
                newCache.data.unshift(...data);
                newCache.bounds = newCache.bounds.union(range)[0];
            } else {
                newCache.data.push(...data);
                newCache.bounds = newCache.bounds.union(range).at(-1);
            }
        }

        // update bounds
        newCache.bounds = fullCacheBounds;

        // slice out unneeded data
        let zeroedBound = fullCacheBounds.shift(-newCache.bounds.start);
        newCache.data = newCache.data.slice(zeroedBound.start, zeroedBound.end);

        // save last request
        newCache.lastRequest = request;

        return newCache;
    }

    function generateResult(cachedData, request) {
        let dataBounds = new Bound(request.start, request.start + request.length).shift(
            -cachedData.bounds.start
        );

        let result = {
            draw: request.draw,
            recordsTotal: cachedData.recordsTotal,
            recordsFiltered: cachedData.recordsFiltered,
            data: cachedData.data.slice(dataBounds.start, dataBounds.end),
        };

        return result;
    }

    return async function (request, drawCallback) {
        // gather request info
        let cachedData = await getUpdateCache(request);

        // generate result from given cache
        let result = generateResult(cachedData, request);

        // call callback with sliced data from cache
        drawCallback(result);
    };
};

export default pipeline;
